import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Settings, ToggleLeft, ToggleRight, Save, RefreshCw, Truck, Clock, Megaphone, Plus, X, Image,
  Shield, RotateCcw, Package, Lock, Star, Zap, CheckCircle, Gift, Award, Trash2, XCircle,
} from "lucide-react";
import ImageUploadInput from "../../components/ImageUploadInput";
import { invalidateStoreSettings } from "../../hooks/useStoreSettings";
import InvoiceSettingsSection from "./InvoiceSettingsSection";

const DEFAULT_TOP_STRIP_ITEMS = ["Free Shipping Pan India", "COD Available", "Easy Returns", "Secure Payments"];

const BADGE_ICON_OPTIONS = [
  { value: "truck",        label: "Truck (Delivery)" },
  { value: "rotateccw",    label: "Return / Exchange" },
  { value: "xcircle",      label: "X Circle (Non-Returnable)" },
  { value: "lock",         label: "Lock (Secure)" },
  { value: "shield",       label: "Shield (Protection)" },
  { value: "award",        label: "Award (Quality)" },
  { value: "package",      label: "Package (Delivered)" },
  { value: "star",         label: "Star (Rating)" },
  { value: "zap",          label: "Zap (Fast)" },
  { value: "checkcircle",  label: "Check (Verified)" },
  { value: "clock",        label: "Clock (On Time)" },
  { value: "gift",         label: "Gift (Offer)" },
];

const BADGE_ICON_MAP = {
  truck: Truck, rotateccw: RotateCcw, lock: Lock, shield: Shield,
  award: Award, package: Package, star: Star, zap: Zap,
  checkcircle: CheckCircle, clock: Clock, gift: Gift, xcircle: XCircle,
};

const DEFAULT_TRUST_BADGES = [
  { icon: "truck",     label: "Free\nDelivery",             enabled: true },
  { icon: "rotateccw", label: "10 days Return\n& Exchange", enabled: true },
  { icon: "package",   label: "Brand\nDelivered",           enabled: true },
  { icon: "lock",      label: "Secure\nTransaction",        enabled: true },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400" />
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{title}</p>
        {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function SettingToggle({ label, description, value, onChange, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        data-testid={testId}
        onClick={() => onChange(!value)}
        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
          value
            ? "bg-orange-500 text-white border-orange-500 hover:bg-orange-600"
            : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"
        }`}
      >
        {value
          ? <><ToggleRight className="w-3.5 h-3.5" /> Enabled</>
          : <><ToggleLeft className="w-3.5 h-3.5" /> Disabled</>}
      </button>
    </div>
  );
}

function formatHour(h) {
  if (h === 0) return "12:00 AM (Midnight)";
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return "12:00 PM (Noon)";
  return `${h - 12}:00 PM`;
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const token = localStorage.getItem("it_token");

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      setDirty(false);
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (key, val) => {
    setSettings(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      invalidateStoreSettings(); // bust cache so SiteHeader picks up new logo
      toast.success("Settings saved");
      setDirty(false);
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
      </div>
    );
  }

  const deliveryDays = settings?.default_delivery_days ?? 2;
  const cutoffHour = settings?.order_cutoff_hour ?? 18;

  return (
    <div className="p-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Storefront Settings</h1>
            <p className="text-xs text-slate-500">Control what visitors see on your product pages</p>
          </div>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-600 transition-colors" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Brand Identity */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
        <SectionHeader icon={Image} title="Brand Identity" subtitle="Logo shown in the storefront header — favicon shown in browser tabs" />
        <div className="px-5 py-5 space-y-5">

          {/* Logo */}
          <div>
            <p className="text-sm font-semibold text-slate-800 mb-1">Store Logo</p>
            <p className="text-xs text-slate-500 mb-3">Displayed in the top-left of the storefront header. Recommended: PNG with transparent background, height 40–60 px.</p>
            <div className="flex items-start gap-4">
              <div className="w-32 h-14 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {settings?.logo_url
                  ? <img src={settings.logo_url} alt="Logo preview" className="max-w-full max-h-full object-contain p-1" />
                  : <p className="text-[10px] text-slate-400 text-center px-2">No logo<br />uploaded</p>}
              </div>
              <div className="flex-1">
                <ImageUploadInput
                  value={settings?.logo_url || ""}
                  onChange={v => handleChange("logo_url", v)}
                  placeholder="Paste logo URL or click Upload"
                />
                {settings?.logo_url && (
                  <button onClick={() => handleChange("logo_url", "")}
                    className="mt-1.5 text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
                    <X className="w-3 h-3" /> Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Favicon */}
          <div>
            <p className="text-sm font-semibold text-slate-800 mb-1">Favicon</p>
            <p className="text-xs text-slate-500 mb-3">Shown in browser tabs and bookmarks. Recommended: 32×32 px or 64×64 px PNG/ICO.</p>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {settings?.favicon_url
                  ? <img src={settings.favicon_url} alt="Favicon preview" className="w-8 h-8 object-contain" />
                  : <p className="text-[10px] text-slate-400 text-center">No<br />favicon</p>}
              </div>
              <div className="flex-1">
                <ImageUploadInput
                  value={settings?.favicon_url || ""}
                  onChange={v => handleChange("favicon_url", v)}
                  placeholder="Paste favicon URL or click Upload"
                />
                {settings?.favicon_url && (
                  <button onClick={() => handleChange("favicon_url", "")}
                    className="mt-1.5 text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
                    <X className="w-3 h-3" /> Remove favicon
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Brand Color Picker */}
          <div>
            <p className="text-sm font-semibold text-slate-800 mb-1">Brand Color</p>
            <p className="text-xs text-slate-500 mb-3">Primary accent color used across the storefront — footer, sidebar active states, links, and CTA buttons.</p>
            <div className="flex items-center gap-4 flex-wrap">
              {/* Preset swatches */}
              {["#e86f28","#2563eb","#16a34a","#dc2626","#7c3aed","#0891b2","#f59e0b","#db2777"].map(hex => (
                <button
                  key={hex}
                  onClick={() => {
                    handleChange("brand_color", hex);
                    document.documentElement.style.setProperty("--brand", hex);
                  }}
                  title={hex}
                  className="w-8 h-8 rounded-full border-2 transition-all hover:scale-110"
                  style={{ backgroundColor: hex, borderColor: (settings?.brand_color || "#e86f28") === hex ? "#0f172a" : "transparent" }}
                  data-testid={`brand-color-swatch-${hex.replace("#","")}`}
                />
              ))}
              {/* Full color picker */}
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="color"
                  value={settings?.brand_color || "#e86f28"}
                  onChange={e => {
                    handleChange("brand_color", e.target.value);
                    document.documentElement.style.setProperty("--brand", e.target.value);
                  }}
                  className="w-8 h-8 rounded cursor-pointer border border-slate-200"
                  data-testid="brand-color-picker"
                />
                <span className="text-xs text-slate-500 group-hover:text-slate-700">Custom</span>
              </label>
              {/* Current color display */}
              <div className="flex items-center gap-2 ml-2">
                <div className="w-5 h-5 rounded" style={{ backgroundColor: settings?.brand_color || "#e86f28" }} />
                <span className="text-xs font-mono text-slate-600">{settings?.brand_color || "#e86f28"}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Product Page Trust Badges */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
        <SectionHeader icon={Shield} title="Product Page Trust Badges" subtitle="Icons shown below the Add to Cart button on every product page" />
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-slate-500">These are the <strong>global defaults</strong> — shown on all products unless a product has its own badges set. The Return badge is automatically hidden on products marked as Non-returnable.</p>
          {/* Return Window Days */}
          <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-xl">
            <RotateCcw className="w-4 h-4 text-orange-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-slate-800">Return Window (days)</p>
              <p className="text-[11px] text-slate-500">Updates the return badge label across all products automatically</p>
            </div>
            <input
              type="number"
              min="1"
              max="365"
              value={settings?.return_window_days ?? "10"}
              onChange={e => handleChange("return_window_days", e.target.value)}
              className="w-16 border border-orange-300 rounded-lg px-2 py-1.5 text-sm font-bold text-center focus:outline-none focus:border-orange-500 bg-white"
            />
            <span className="text-xs text-slate-500 flex-shrink-0">days</span>
          </div>
          {(settings?.default_trust_badges || DEFAULT_TRUST_BADGES).map((badge, idx) => {
            const allBadges = settings?.default_trust_badges?.length ? settings.default_trust_badges : DEFAULT_TRUST_BADGES;
            const IconComp = BADGE_ICON_MAP[badge.icon] || Shield;
            return (
              <div key={idx} className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl bg-slate-50/60">
                {/* Preview icon */}
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <IconComp className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
                </div>
                {/* Enable toggle */}
                <button
                  type="button"
                  onClick={() => handleChange("default_trust_badges", allBadges.map((b, i) => i === idx ? { ...b, enabled: !b.enabled } : b))}
                  className={`flex-shrink-0 w-10 h-5 rounded-full transition-all relative ${badge.enabled !== false ? "bg-orange-500" : "bg-slate-300"}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${badge.enabled !== false ? "left-5" : "left-0.5"}`} />
                </button>
                {/* Icon selector */}
                <select
                  value={badge.icon || "truck"}
                  onChange={e => handleChange("default_trust_badges", allBadges.map((b, i) => i === idx ? { ...b, icon: e.target.value } : b))}
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-orange-500 flex-shrink-0"
                >
                  {BADGE_ICON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                {/* Label */}
                <input
                  type="text"
                  value={badge.label || ""}
                  onChange={e => handleChange("default_trust_badges", allBadges.map((b, i) => i === idx ? { ...b, label: e.target.value } : b))}
                  placeholder="Badge label..."
                  className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 bg-white"
                />
                {/* Delete */}
                <button
                  type="button"
                  onClick={() => handleChange("default_trust_badges", allBadges.filter((_, i) => i !== idx))}
                  className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
                ><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => {
              const allBadges = settings?.default_trust_badges?.length ? [...settings.default_trust_badges] : [...DEFAULT_TRUST_BADGES];
              handleChange("default_trust_badges", [...allBadges, { icon: "shield", label: "New Badge", enabled: true }]);
            }}
            className="flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium border border-orange-300 hover:border-orange-400 px-3 py-1.5 rounded-lg transition-all"
          >
            <Plus className="w-4 h-4" /> Add Badge
          </button>
        </div>
      </div>

      {/* Cart & Conversion */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
        <SectionHeader icon={Settings} title="Cart & Conversion" />
        <div className="px-5">
          <SettingToggle
            label="Sticky Add to Cart Bar"
            description="Shows a persistent 'Add to Cart' and 'Buy Now' bar at the bottom when the main buttons scroll out of view — improves conversion on long pages."
            value={settings?.storefront_sticky_cta ?? true}
            onChange={(val) => handleChange("storefront_sticky_cta", val)}
            testId="setting-sticky-cta-toggle"
          />
        </div>
      </div>

      {/* Product Page Top Strip */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
        <SectionHeader icon={Megaphone} title="Product Page Top Strip" subtitle="The thin bar at the very top of every product page" />
        <div className="px-5">
          <SettingToggle
            label="Show Top Strip"
            description="Turn the product page top strip (e.g. Free Shipping Pan India, COD Available) on or off."
            value={settings?.product_top_strip_enabled ?? true}
            onChange={(val) => handleChange("product_top_strip_enabled", val)}
            testId="setting-top-strip-toggle"
          />
          {(settings?.product_top_strip_enabled ?? true) && (() => {
            const items = settings?.product_top_strip_items ?? DEFAULT_TOP_STRIP_ITEMS;
            const setItems = (next) => handleChange("product_top_strip_items", next);
            return (
              <div className="py-4 space-y-2" data-testid="setting-top-strip-items">
                <p className="text-sm font-semibold text-slate-800">Strip Texts</p>
                <p className="text-xs text-slate-500">Shown left to right. On smaller screens only the first items fit.</p>
                {items.map((text, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={text}
                      onChange={e => setItems(items.map((t, j) => (j === i ? e.target.value : t)))}
                      data-testid={`setting-top-strip-item-${i}`}
                      className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-orange-500 text-slate-800"
                    />
                    <button
                      onClick={() => setItems(items.filter((_, j) => j !== i))}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-300 text-slate-400 hover:text-red-500 hover:border-red-300"
                      title="Remove"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {items.length < 6 && (
                  <button
                    onClick={() => setItems([...items, ""])}
                    className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add text
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Delivery Defaults */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6">
        <SectionHeader
          icon={Truck}
          title="Delivery Defaults"
          subtitle="Used when a product has no per-product delivery setting"
        />
        <div className="px-5 divide-y divide-slate-100">

          {/* Delivery Days */}
          <div className="py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">Default Delivery Days</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  Expected delivery window shown on the product page (e.g., "FREE delivery Tuesday, 14 Jan").
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleChange("default_delivery_days", Math.max(1, deliveryDays - 1))}
                  className="w-8 h-8 border border-slate-300 rounded-lg flex items-center justify-center hover:bg-slate-50 transition-colors text-slate-600 font-bold disabled:opacity-30"
                  disabled={deliveryDays <= 1}
                >−</button>
                <span
                  className="w-10 text-center text-sm font-bold text-slate-900 tabular-nums"
                  data-testid="delivery-days-value"
                >
                  {deliveryDays}d
                </span>
                <button
                  onClick={() => handleChange("default_delivery_days", Math.min(30, deliveryDays + 1))}
                  className="w-8 h-8 border border-slate-300 rounded-lg flex items-center justify-center hover:bg-slate-50 transition-colors text-slate-600 font-bold disabled:opacity-30"
                  disabled={deliveryDays >= 30}
                >+</button>
              </div>
            </div>
          </div>

          {/* Order Cutoff Time */}
          <div className="py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">Order Cutoff Time</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  "Order within X hrs Y mins" countdown target on the product page.
                  <br />Currently: <span className="font-medium text-slate-700">{formatHour(cutoffHour)}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Clock className="w-4 h-4 text-slate-400" />
                <select
                  data-testid="cutoff-hour-select"
                  value={cutoffHour}
                  onChange={e => handleChange("order_cutoff_hour", parseInt(e.target.value))}
                  className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:border-orange-500 bg-white text-slate-800"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{formatHour(i)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

        </div>
      </div>

      <InvoiceSettingsSection settings={settings} onChange={handleChange} />

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          data-testid="settings-save-btn"
          onClick={handleSave}
          disabled={!dirty || saving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            dirty
              ? "bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          {saving
            ? <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {dirty && <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>}
      </div>
    </div>
  );
}
